import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './Menu.css';

/**
 * The same popover as `Menu`, opened by a right-click instead of a button.
 * Portalled to the body because it has to escape the panel's own scroll box,
 * and closed by a scroll for the same reason — a menu pinned to a viewport
 * position while its thumbnail slides away is pointing at nothing.
 *
 * The `contextmenu` event is also what the keyboard's menu key fires, so
 * this is reachable without a mouse.
 */
export function ContextMenu({
  x,
  y,
  label,
  onClose,
  children,
}: {
  x: number;
  y: number;
  label: string;
  onClose: () => void;
  children: (close: () => void) => ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    root.current?.querySelector('button')?.focus();

    function onPointerDown(event: PointerEvent) {
      if (!(event.target instanceof Node)) return;
      if (root.current?.contains(event.target) === false) onClose();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // The panel closes on Escape too; this one is nearer the front.
        event.stopPropagation();
        onClose();
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const width = 200;
  const left = Math.min(x, window.innerWidth - width - 8);
  const top = Math.min(y, window.innerHeight - 96);

  return createPortal(
    <div
      ref={root}
      className="menu__list menu__list--floating"
      role="menu"
      aria-label={label}
      style={{ left: `${Math.max(8, left)}px`, top: `${Math.max(8, top)}px`, minWidth: `${width}px` }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onClose();
      }}
    >
      {children(onClose)}
    </div>,
    document.body,
  );
}
